import { useQueryClient } from '@tanstack/react-query';
import React, { FC } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { ContentLoadError, PageTitle, PageWrapper, Spinner } from '../../../components';
import { useHeroToast, useWarmup } from '../../../hooks';
import {
  useGetAttractionDocumentFromFirestore,
  usePatchAttractionFirestore,
  useQueryHelper,
} from '../../../queries';
import { AttractionsFirestoreDocumentRequest } from '../../../types';
import { AttractionWizard } from '../components';

export const AttractionEdit: FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showError, showSuccess } = useHeroToast();

  const attractionDocument = useGetAttractionDocumentFromFirestore(id);

  const { data: attraction, isLoading: loading, refetch } = attractionDocument;
  const { isLoading, isError, errorMessage } = useQueryHelper({
    queries: [attractionDocument],
  });

  const { mutate: editAttraction, isPending } = usePatchAttractionFirestore();
  useWarmup('v3-updateAttraction');
  const queryClient = useQueryClient();
  const handleSubmit = async (
    data: AttractionsFirestoreDocumentRequest,
  ): Promise<void> => {
    editAttraction(
      {
        data: { ...data, baseLang: 'pl' },
        id: id || '',
      },
      {
        onSuccess: () => {
          showSuccess('Attraction edit successfully!');
          navigate('/attractions');

          queryClient.resetQueries({
            queryKey: ['attractions'],
            exact: true, // tylko lista
          });
          queryClient.invalidateQueries({
            queryKey: [`attraction-${id}`],
            exact: true,
          });
        },
        onError: (error) => {
          console.error('Error editing attraction:', error);
          showError('Something went wrong. The attraction wasn\'t edit.');
        },
      },
    );
  };

  if (isLoading) {
    return <Spinner global />;
  }

  if (isError) {
    return (
      <PageWrapper>
        <ContentLoadError onRefreshClicked={() => { refetch(); }} message={errorMessage} />
      </PageWrapper>
    );
  }

  // Map API response to form structure
  const mappedInitialValues = attraction ? {
    ...attraction,
    status: attraction.status || 'PREVIEW',
  } : undefined;

  return (
    <PageWrapper>
      <PageTitle title="Edit Attraction" />
      {loading && attraction === undefined ? (
        <Spinner global />
      ) : (
        <AttractionWizard
          onSubmit={handleSubmit}
          initialValues={mappedInitialValues as any}
          isEdit
          isSubmitting={isPending}
        />
      )}
    </PageWrapper>
  );
};
